import React from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

// Components
import Container from 'components/common/Layout/Container';
import Main from 'components/common/Layout/Main';
import Center from 'components/templates/center';
import Head from 'next/head';

// Libs
import { appConfig, apiConfig } from 'configs';
import Translation from 'libs/Translation';
import Util from 'libs/Util';

const CENTER = ({ center = {}, pageFAQ = [] }) => {
	const router = useRouter();
	const {webConfig} = useSelector(state => state.core);
	const { query } = router;

	const { seo: { title, description }, addTitlePostfix } = Util.prepareSeo(
		'center',
		{
			name: Translation.t('text.center-title', {name: center.Name, city: webConfig.lang === 'fa' ? center?.City.Title : center?.City.Slug} ),
			description: Translation.t('text.center-description', {name: center.Name, city: webConfig.lang === 'fa' ? center?.City.Title : center?.City.Slug}),
		},
	);

	const breadCrumb = {
		itemListElements: [
			{
				position: 1,
				name: Translation.t('title.default'),
				item: 'https://vclinic.io',
			},
			{
				position: 2,
				name: `${center.Name}`,
				item: `https://vclinic.io/center/${query.name}`,
			},
		],
	};

	const seo = {
		title: `${title} | ${Translation.t('title.default')}`,
		h1Title: title,
		description,
		additionalMetaTags: [
			{ name: 'twitter:card', content: 'summary' },
			{ name: 'twitter:site', content: '@vclinic_io' },
			{ name: 'twitter:title', content: `${title} | ${Translation.t('title.default')}` },
			{ name: 'twitter:description', content: description },
			{ name: 'twitter:creator', content: '@vclinic_io' },
			{ name: 'twitter:image', content: center?.profileImage ? center.profileImage?.fileUrl : 'https://vclinic.io/images/center/avatar.jpeg' },
		],
		openGraph: {
			title: `${title} | ${Translation.t('title.default')}`,
			description,
			type: 'profile',
			site_name: Translation.t('label.virtual-clinic'),
			images: [
				{
					url: center?.profileImage ? center.profileImage?.fileUrl : 'https://vclinic.io/images/center/avatar.jpeg',
					type: 'image/jpg',
					alt: center.Name,
					width: '250',
					height: '250',
				},
			],
		},
	};
	
    return (
		<Main
			breadCrumb={breadCrumb} 
			seo={seo} 
			addTitlePostfix={addTitlePostfix}
		>
			<Head>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
					__html: JSON.stringify(
						Util.makeCenterProfileJsonLd(
							center,
							router.asPath,
							webConfig.lang === 'fa' ? center?.City.Title : center?.City.Slug
						)),
				}}
				/>
			</Head>
			<Container>
            	<Center 
					center={center}
					pageFAQ={pageFAQ}
					webConfig={webConfig}
				/>
        	</Container>
		</Main>
        
    )
}

export default CENTER;

export const getServerSideProps = async ({ query }) => {
	if (query.name) {
		let result;
		let data;
		let notFound = false;

		let lang = 'fa';
		if (query.lang) {
			lang = query.lang
		}
		let queries = `?Slug=${query.name}&Lang=${lang}`;
		
		try {
			result = await Promise.all([
				fetch(`${appConfig.node_api_base_url}${apiConfig.HPProfile.endpoint}${queries}`),
                fetch(`${appConfig.node_api_base_url}${apiConfig.getFAQ.endpoint}?lang=${lang}&page=/center/[name]`)
			]);

			if (result) {
				data = await Promise.all([
					result[0].json(),
					result[1].json()
				]);
			}
			if (data[0]?.message) {
				notFound = true;
			}
			
		}
		catch (error) {
			if (error) {
				return {
					notFound: true,
				};
			}
		}
	
		return {
			props: {
				center: data[0]?.profiles?.length ? data[0]?.profiles[0] : {},
				pageFAQ: data[1].length ? data[1][0]?.FAQ : []
			},
			notFound: notFound,
		};
	}
};